import type { AppConfig, ResourceSubject } from '../types';

/**
 * Resource-to-App Resolver Service
 *
 * **Primary Use Case**: Task deep linking - matches task subject identifiers to apps
 * in the registry, enabling rewriting of absolute deep link URLs into PSP-friendly
 * internal routes for seamless navigation within the PSP shell.
 *
 * **Generic Purpose**: Resolves resource subjects (tasks, notifications, etc.) to their
 * corresponding apps. Returns the full AppConfig to enable multiple use cases:
 * - Extracting basePath for URL rewriting (current primary use)
 * - Getting displayName or icon for UI display (future use)
 * - Any other app metadata needed for resource handling
 *
 * **Subject Format**: Uses URN format `urn:opengov:{resource}:{segments}*`
 *
 * **Slash Handling**: The resolver strips everything after the first slash before pattern
 * matching. This works best when slashes appear only in the final segment of a URN (after
 * all colon-delimited structural segments).
 *
 * Examples:
 * - `urn:opengov:gl:journal-entry:12345` (all colons) - matches patterns reliably
 * - `urn:opengov:gl:journal-entry/12345` (slash in final segment) - matches patterns reliably
 * - `urn:opengov:procurement:purchase-order/12345/details` (slashes in final segment) - matches patterns reliably
 * - `urn:opengov:gl/journal-entry:12345` (slash in middle segment) - may not match specific patterns
 */

/**
 * Parses a resource subject string into components.
 *
 * Expected format: urn:opengov:{resource}:{segment}*
 *
 * **Note**: The caller should normalize the subject by stripping everything after the
 * first slash before calling this function (see resolveResourceToApp for implementation).
 *
 * @param subject - Resource subject string to parse (should already be normalized)
 * @returns Parsed components or null if invalid
 *
 * @example
 * parseResourceSubject("urn:opengov:procurement:po")
 * // Returns: { scheme: 'urn', namespace: 'opengov', resource: 'procurement', segments: ['po'], original: '...' }
 *
 * @example
 * parseResourceSubject("urn:opengov:gl:journal-entry")
 * // Returns: { scheme: 'urn', namespace: 'opengov', resource: 'gl', segments: ['journal-entry'], original: '...' }
 */
export function parseResourceSubject(subject: string): ResourceSubject | null {
  if (!subject || typeof subject !== 'string') {
    return null;
  }

  const parts = subject.split(':');

  // Minimum valid URN: urn:opengov:resource
  if (parts.length < 3) {
    console.warn('[Resource Resolver] Invalid subject format (too few segments):', subject);
    return null;
  }

  const [scheme, namespace, resource, ...segments] = parts;

  if (scheme !== 'urn') {
    console.warn('[Resource Resolver] Invalid subject scheme (expected "urn"):', subject);
    return null;
  }

  if (namespace !== 'opengov') {
    console.warn('[Resource Resolver] Invalid subject namespace (expected "opengov"):', subject);
    return null;
  }

  if (!resource) {
    console.warn('[Resource Resolver] Invalid subject (missing resource):', subject);
    return null;
  }

  return {
    scheme: 'urn' as const,
    namespace: 'opengov' as const,
    resource,
    segments,
    original: subject,
  };
}

/**
 * Calculates specificity score for a resource subject pattern.
 * More specific patterns (more segments) get higher scores.
 *
 * Similar to moduleResolver.ts route specificity.
 */
function calculatePatternSpecificity(pattern: string): number {
  const parsed = parseResourceSubject(pattern);
  if (!parsed) return 0;

  // Base score for having a resource
  let score = 10;

  // Add points for each additional segment
  score += parsed.segments.length * 5;

  return score;
}

/**
 * Checks if a resource subject matches a pattern.
 * Patterns match if they are a prefix of the subject (allowing over-specification).
 *
 * @example
 * matchesPattern("urn:opengov:procurement", "urn:opengov:procurement:po") // true
 * matchesPattern("urn:opengov:financial:gl", "urn:opengov:financial:general-ledger") // false
 */
function matchesPattern(pattern: string, subject: string): boolean {
  // Exact match
  if (pattern === subject) {
    return true;
  }

  // Prefix match (pattern is shorter, subject has extra segments)
  // Pattern: "urn:opengov:procurement"
  // Subject:     "urn:opengov:procurement:po:12345"
  if (subject.startsWith(pattern + ':')) {
    return true;
  }

  return false;
}

interface ResourceMatchResult {
  app: AppConfig;
  pattern: string;
  specificity: number;
}

/**
 * Resolves a resource subject to the app that handles it.
 * Uses specificity scoring when multiple apps match.
 *
 * Returns the full AppConfig to enable extracting basePath, displayName, icon,
 * or any other app metadata as needed.
 *
 * @param subject - Resource subject string to match
 * @param appRegistry - Array of app configurations
 * @returns Matching app config or null if no match found
 */
export function resolveResourceToApp(
  subject: string,
  appRegistry: readonly AppConfig[]
): AppConfig | null {
  // Normalize subject by stripping everything after the first slash for pattern matching
  const normalizedSubject = subject.split('/')[0];

  // Parse the normalized subject (not the original)
  const parsedSubject = parseResourceSubject(normalizedSubject);
  if (!parsedSubject) {
    return null;
  }

  const matches: ResourceMatchResult[] = [];

  for (const app of appRegistry) {
    if (!app.resourceSubjectPatterns || app.resourceSubjectPatterns.length === 0) {
      continue;
    }

    for (const pattern of app.resourceSubjectPatterns) {
      if (matchesPattern(pattern, normalizedSubject)) {
        matches.push({
          app,
          pattern,
          specificity: calculatePatternSpecificity(pattern),
        });
      }
    }
  }

  if (matches.length === 0) {
    console.warn('[Resource Resolver] No app found for subject:', subject);
    return null;
  }

  // Sort by specificity (descending) and return the most specific match
  matches.sort((a, b) => b.specificity - a.specificity);

  if (matches.length > 1 && matches[0].specificity === matches[1].specificity) {
    console.warn(
      '[Resource Resolver] Multiple apps with equal specificity for subject:',
      subject,
      'Using first match:',
      matches[0].app.id
    );
  }

  return matches[0].app;
}
